/** Query prefixes shared by the app's bill tracker, whip boards and dossiers.
 *
 * A durable event identifies the changed row, not necessarily its parent bill,
 * member or project. Invalidate workspace prefixes so both immediate events
 * and outbox replay refresh the same views without guessing parent IDs.
 */
export function workspaceWorkflowKeys(event: {
  entity_type: string;
  workspace_id?: string;
  entity_id?: string;
}): string[][] {
  const ws = event.workspace_id;
  if (!ws) return [];
  const board = () => [
    ["whip-count", ws],
    ["whip-count-detail", ws],
    ["whip-math", ws],
    ["whip-rack", ws],
    ["map-data", ws],
    ["widget", "bills.whip_math", ws],
    // Board index cards carry stance counts; the member dossier rolls up
    // staff-set stances across every readable board.
    ["whip-boards", ws],
    ["member-whip-rollup"],
  ];
  const dossier = () => [["bill-intelligence", ws]];
  switch (event.entity_type) {
    case "whip_stance":
      return [...board(), ...dossier()];
    case "signal":
      return [...board(), ...dossier(), ["member-portal", ws]];
    case "member_touch":
    case "member":
      return [...board(), ["member-portal", ws], ["member-scores", ws]];
    case "contact":
    case "contact_affiliation":
    case "contact_note":
    case "engagement":
    case "activity":
      return [...board(), ...dossier(), ["member-portal", ws], ["contact-portal", ws]];
    case "issue_bill_rating":
    case "issue":
      return [...board(), ...dossier(), ["issue-portal", ws]];
    case "project":
      return [
        ...board(), ...dossier(),
        ["tracked-bills", ws], ["tracked-bills-dash", ws],
        ["widget", "bills.tracked_recent", ws],
        ["widget", "bills.stalled", ws],
      ];
    case "bill_note":
      return [...dossier(), ["whip-count-detail", ws]];
    case "bill_resource":
    case "organization_position":
      return dossier();
    case "proposed_tally":
    case "proposed_tally_entry":
      return board();
    case "whip_board":
      // Standalone board rows (title/description live on the backing issue,
      // which emits "issue"); creation and import land here.
      return [["whip-boards", ws], ["whip-count", ws], ["map-data", ws]];
    case "whip_stance_proposal":
      // Review queue, "my proposals" and the board's pending badge. Approval
      // writes the stance itself, which emits whip_stance separately.
      return [["whip-proposals"], ["whip-count", ws]];
    case "whip_board_access":
      // Per-board grants and join requests: share dialog, review queue,
      // shared-with-me and the board's access summary.
      return [
        ["whip-board-access", ws], ["whip-board-access-summary", ws],
        ["whip-join-requests", ws], ["whip-boards", "shared-with-me"],
        ["whip-boards", "my-join-requests"],
      ];
    case "whip_board_publication":
      return [["public-board", ws]];
    case "whip_discussion":
    case "whip_discussion_bookmark":
    case "whip_board_follow":
      // Board/member threads and the workspace discussions hub.
      return [["whip-discussion", ws], ["discussions", ws]];
    case "workspace_event":
    case "workspace_events":
      return [["workspace-activity", ws]];
    case "amendment_watch":
      return [["amendments", ws], ["widget", "watched_amendments", ws], ...dossier()];
    case "workspace_provision":
      return [
        ["provisions", ws], ["provision-detail", ws],
        ["widget", "provisions_tracker", ws], ["amendments", ws],
      ];
    default:
      return [];
  }
}
